import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  AvatarGroup,
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spacer,
  Spinner,
  Stack,
  StackDivider,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/core';
import { SearchIcon } from '@chakra-ui/icons';
import { css } from '@emotion/core';
import _isNil from 'lodash/isNil';
import _sortBy from 'lodash/sortBy';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { IoMdRefresh, IoMdChatbubbles } from 'react-icons/io';
import { IoEye } from 'react-icons/io5';
import { MdArrowBack, MdPerson, MdPersonAddAlt1 } from 'react-icons/md';
import { RiInboxUnarchiveLine, RiShareFill, RiUserStarLine } from 'react-icons/ri';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { AiFillClockCircle } from 'react-icons/ai';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import useContactEmailForAccountAuthorisation from '../../../../app/hooks/useContactEmailForAccountAuthorisation';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { PATH_POSITION_TABS } from '../../../../routes/constants';
import colors from '../../../../styles/colors';
import { ProfilePicture } from '../../../common';
import { SelectedTab } from '../../topCandidates/TopCandidatesContext';
import { Business } from '../BusinessType';
import { defaultPositionListParams, usePositionManagementContext } from '../PositionManagementContext';
import {
  ConfirmMessageType,
  FollowedRecruiters,
  GhostedRecruiter,
  JobPosition,
  JopPositionResponse,
  PositionFilterEnum,
  PositionFromPositionList,
  PositionListQueryParams,
  PositionListResponse,
} from '../PositionType';
import { AutomationTypes } from '../createJobs/AutomationTypes';
import { CreateJobsView } from '../createJobs/CreateJobsView';
import { NewCreateJobsView } from '../createJobs/NewCreateJobsView';
import { PositionAutomationInfoView } from './PositionAutomationInfoView';
import PositionAutomationSelect from './PositionAutomationSelect';
import { PositionFollowButton } from './PositionFollowButton';
import { PositionLinkModal } from './PositionLinkModel';
import { PositionRequisitionMenuButton } from './PositionRequisitionMenuButton';
import { UpdateStatusConfirmation } from './UpdateStatusConfirmation';
import { NotificationTypes } from '../../../../firebase/firestore/documents/notificationTypes';
import { PositionWatchButton } from './PositionWatchButton';
import { checkIfUserRestrictedInPositions } from '../../../../services/userService';

const selectStyle = css`
  margin-right: 1.2rem;
  .chakra-select__icon-wrapper {
    border-left: 1px solid rgb(255 255 255 / 31%);
  }
`;

export type JobsListProps = {
  activeBusiness: Business | null;
  isAdministrationAccount: boolean;
  changeActiveBusiness: (business: Business | null) => void;
  setScrollToBusiness: (business: Business) => void;
  filterType: 'all' | 'followed' | 'watched' | 'followAndWatch';
};

export enum InfoType {
  OPEN = 'OPEN',
  CLOSE = 'CLOSE',
  ARCHIVE = 'ARCHIVE',
  UNARCHIVE = 'UNARCHIVE',
}
type PositionsData = PositionListResponse['positions'];

export const PositionList = ({
  activeBusiness,
  changeActiveBusiness,
  isAdministrationAccount,
  setScrollToBusiness,
  filterType,
}: JobsListProps): JSX.Element => {
  const defaultJobListResponse: PositionListResponse = {
    ghostedRecruiters: [],
    positions: {
      count: 0,
      lastPage: 1,
      to: 0,
      from: 0,
      data: [],
    },
  };
  const defaultPositionListData: PositionsData = defaultJobListResponse.positions;

  const { t } = useTranslation();
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const isCurrentPage = (path: string): boolean => match.url.includes(path);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isArchivingPosition, setIsArchivingPosition] = useState<boolean>(false);
  const [isUnArchivingPosition, setIsUnArchivingPosition] = useState<boolean>(false);
  const [isUpdatingStatusId, setUpdatingStatusId] = useState({} as { [key: string]: boolean });
  const [isArchivingStatusId, setArchivingStatusId] = useState({} as { [key: string]: boolean });
  const [isUnArchivingStatusId, setUnArchivingStatusId] = useState({} as { [key: string]: boolean });
  const [isUpdatingFollow, setIsUpdatingFollow] = useState({} as { [key: string]: boolean });
  const [positionData, setPositionData] = useState<PositionsData>(defaultPositionListData);
  const [searchText, setSearchText] = useState<string>('');
  const [ghostedRecruiters, setGhostedRecruiters] = useState<GhostedRecruiter[]>([]);
  const ghostedRecruiterIds = useMemo(() => new Set(ghostedRecruiters.map((g) => g.id)), [ghostedRecruiters]);
  const [assignedRecruiters, setAssignedRecruiters] = useState<FollowedRecruiters[] | undefined>(undefined);
  const [recuriterImages, setRecuritersImages] = useState<Map<string, string>>(new Map<string, string>());
  const [selectedPosition, setSelectedPosition] = useState<PositionFromPositionList | null>(null);
  const [confirmUpdateStatusMessage, setConfirmUpdateStatusMessage] = useState<ConfirmMessageType | null>(null);
  const [noOfUpdatedPosition, setNoOfUpdatedPosition] = useState<number>(0);
  const [openPositionCountOfAllPosition, setOpenPositionCountOfAllPosition] = useState<number>(0);
  const [createNewPosition, setCreateNewPosition] = useState<boolean>(false);
  const [filterByRequisition, setFilterByRequisition] = useState<boolean>(false);
  const [currentPositionId, setCurrentPositionId] = useState<string>('');

  const [positionLinkOpen, setPositionLinkOpen] = useState<boolean>(false);
  const [popOverMessage, setPopOverMessage] = useState<string>('');
  const positionRef = useRef<null | HTMLDivElement>(null);
  const [positionToEdit, setPositionToEdit] = useState<JopPositionResponse | null>(null);

  const refreshPositionPageNotification = useStoreState((state) => state.notifications.refreshPositionPageNotification);
  const clearRefreshPositionPageNotification = useStoreActions(
    (state) => state.notifications.clearRefreshPositionPageNotification,
  );

  const refreshPositionPageNotificationCode = refreshPositionPageNotification?.data.code;

  const enableCopyPosition: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_COPY_POSITION];
  });

  const enableNewUiForCreatePosition: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NEW_UI_FOR_CREATE_POSITION];
  });

  const getIsUpdatingFollowFromChild = /* istanbul ignore next */ (status: boolean, positionId: string) => {
    setIsUpdatingFollow((ids) => ({
      ...ids,
      [positionId]: status,
    }));
  };

  const contactEmailForAccountAuthorizaton = useContactEmailForAccountAuthorisation();

  const {
    getPositionList,
    getRecruiterProfilePicture,
    updatePositionStatus,
    updateArchivePosition,
    updateUnArchivePosition,
    getPositionDetail,
    setPositionAutomation,
  } = useStoreActions((actions) => actions.positionManagement);
  const accountId = useStoreState((s) => s.app.user?.account);
  const isRequisitionEnabled = useStoreState((s) => s.app.accounts?.configuration?.requisitionEnabled);
  const authorization = useStoreState((s) => s.app.accounts?.authorization);
  const userId = useStoreState((s) => s.app.user?.id);
  const automationEnabled = useStoreState((s) => s.app.accounts?.subscription?.automation);
  const isAccountVirtualRecruiterEnabled = useStoreState((s) => s.app.accounts?.configuration?.virtualRecruiterEnabled);
  const { positionQueryParams, setPositionQueryParams } = usePositionManagementContext();
  const restrictPositions = useStoreState((s) => s.app.user?.restrictPositions);

  const accounts = useStoreState((s) => s.app.accounts);
  const superOwners = accounts?.superOwners ?? [];
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const appUserId = useStoreState((s) => s.app.user?.id);

  const isUserRestrictedInPositions = checkIfUserRestrictedInPositions({
    appOwnersId,
    appUserId,
    superOwners,
    isUserRestricted: restrictPositions,
  });

  const { isOpen: isPositionLinkOpen, onOpen: onPositionLinkOpen, onClose: onPositionLinkClosed } = useDisclosure({
    defaultIsOpen: true,
  });
  const { isOpen: copyPosition, onOpen: setShowCopyPosition, onClose: setCloseCopyPosition } = useDisclosure();
  const { isOpen: isUpdateStatusModalOpen, onOpen: onUpdateStatusModalOpen, onClose: onUpdateStatusModalClose } = useDisclosure();
  const {
    isOpen: showPositionOpenInfo,
    onOpen: setShowPositionOpenInfo,
    onClose: setShowPositionOpenInfoClose,
  } = useDisclosure();

  const {
    isOpen: showPositionCloseInfo,
    onOpen: setShowPositionCloseInfo,
    onClose: setShowPositionCloseInfoClose,
  } = useDisclosure();

  const {
    isOpen: showPositionArchievedInfo,
    onOpen: setShowPositionArchievedInfo,
    onClose: setShowPositionArchievedInfoClose,
  } = useDisclosure();

  const selectedFilteryType = React.useMemo(() => {
    let result = PositionFilterEnum.ALL;
    if (positionQueryParams.followedOnly) result = PositionFilterEnum.FOLLOWED;
    if (positionQueryParams.watchedOnly) result = PositionFilterEnum.WATCHED;
    if (positionQueryParams.followedOnly && positionQueryParams.watchedOnly) result = PositionFilterEnum.FOLLOWED_WATCHED;
    return result;
  }, [positionQueryParams]);

  const handleInfoBox = (infoType: InfoType) => {
    positionRef?.current?.scrollIntoView();
    // istanbul ignore else
    if (infoType === InfoType.ARCHIVE) {
      setShowPositionArchievedInfo();
    } else if (infoType === InfoType.OPEN) {
      setPopOverMessage(t('jobPosition:info.open'));
      setShowPositionOpenInfo();
    } else if (infoType === InfoType.UNARCHIVE) {
      setPopOverMessage(t('jobPosition:info.unArchive'));
      setShowPositionCloseInfo();
    } else {
      setPopOverMessage(t('jobPosition:info.close'));
      setShowPositionCloseInfo();
    }
    // istanbul ignore next
    setTimeout(() => {
      setShowPositionCloseInfoClose();
      setShowPositionOpenInfoClose();
      setShowPositionArchievedInfoClose();
    }, 3000);
  };

  // istanbul ignore next
  const onCompleteCopy = (business: Business | null) => {
    setCloseCopyPosition();
    if (business) {
      setPopOverMessage(t('jobPosition:info.copy'));
      setShowPositionCloseInfo();
      changeActiveBusiness(business);
    }
    /* istanbul ignore next */
    setTimeout(() => {
      setShowPositionCloseInfoClose();
    }, 3000);
  };

  const getRecuriterImage = async (jopPositionResponse: PositionFromPositionList[]) => {
    const myMap = new Map<string, string>();
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < jopPositionResponse.length; i++) {
      const value = jopPositionResponse[i];
      if (value.assignedRecruiters) {
        // eslint-disable-next-line no-plusplus
        for (let j = 0; j < value.assignedRecruiters.length; j++) {
          // eslint-disable-next-line no-await-in-loop
          const picture = await getRecruiterProfilePicture({ accountId: value.assignedRecruiters[j].id });
          myMap.set(value.assignedRecruiters[j].id, picture);
        }
      }
    }
    setRecuritersImages(myMap);
  };

  /* istanbul ignore next */
  const getQueryParamsPage = (pageNumber: number, positionListCount: number, updatedPositionsCount: number) => {
    if (positionListCount - updatedPositionsCount <= defaultPositionListParams.limit) {
      return pageNumber - 1;
    }
    return pageNumber;
  };

  /* istanbul ignore next */
  const getUpdatedPositionList = (
    existingPositionList: PositionsData,
    responseData: PositionsData,
    updatedPositionsCount: number,
  ) => {
    if (existingPositionList.count - updatedPositionsCount <= defaultPositionListParams.limit) {
      return responseData.data;
    }
    return existingPositionList.data.concat(responseData.data);
  };

  // function to check the interveiw cancellation
  /* istanbul ignore next */
  // const getInterviewCancelCheckBox = () => {
  //   return (
  //     <Checkbox
  //       mb={3}
  //       isChecked={isCheckedInterviewCancel}
  //       onChange={(e) => setIsCheckedInterviewCancel(e.target.checked)}
  //       data-testid="interviewCancelCheck"
  //     />
  //   );
  // };

  const loadPositionList = useCallback(
    async (
      business: Business,
      existingPositionList: PositionsData,
      searchTerms: string,
      noOfUpdatedPositionCount: number,
      loadMore = false,
      filterTypeData?: 'all' | 'followed' | 'watched' | 'followAndWatch',
      newQuery?: PositionListQueryParams,
    ) => {
      setLoading(true);
      const query = { ...(newQuery || positionQueryParams) };
      /* istanbul ignore else */
      if (accountId && business.id) {
        query.page = !loadMore ? 1 : getQueryParamsPage(query.page, existingPositionList.count, noOfUpdatedPositionCount);
        query.searchTerm = searchTerms;
        if (filterTypeData) {
          delete query.watchedOnly;
          query.followedOnly = false;
          if (filterTypeData === 'followed') {
            query.followedOnly = true;
          }
          if (filterTypeData === 'watched') {
            delete query.followedOnly;
            query.watchedOnly = true;
          }
          if (filterTypeData === 'followAndWatch') {
            query.followedOnly = true;
            query.watchedOnly = true;
          }
        }
        setPositionQueryParams({ ...query });

        const data: PositionListResponse = await getPositionList({
          accountId,
          businessId: business?.id,
          params: query,
        });
        setLoading(false);
        setNoOfUpdatedPosition(0);
        /* istanbul ignore else */
        if (data) {
          const {
            positions: positionsResponse = defaultPositionListData,
            ghostedRecruiters: ghostedRecruitersResponse = [],
          } = data;
          setGhostedRecruiters(ghostedRecruitersResponse);
          positionData.data = loadMore
            ? getUpdatedPositionList(existingPositionList, positionsResponse, noOfUpdatedPositionCount)
            : positionsResponse.data;
          positionData.lastPage = positionsResponse.lastPage;
          positionData.count = positionsResponse.count;
          setPositionData({ ...positionData });
          if (query.positionStatus === 'open' && query.followedOnly === false) {
            setOpenPositionCountOfAllPosition(positionsResponse.count);
          }
          await getRecuriterImage(positionData.data);
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, getPositionList, positionQueryParams],
  );

  const loadMorePosition = () => {
    positionQueryParams.page += 1;
    setPositionQueryParams({ ...positionQueryParams });
    /* istanbul ignore else */
    if (!_isNil(activeBusiness)) {
      loadPositionList(activeBusiness, positionData, searchText, noOfUpdatedPosition, true).catch(
        /* istanbul ignore next */ (err) => console.log(err),
      );
    }
  };

  const filterJobPositionList = (value: PositionFilterEnum) => {
    let query: PositionListQueryParams = { ...positionQueryParams };
    if (value === PositionFilterEnum.FOLLOWED) {
      query = omit({ ...query, followedOnly: true, page: 1 }, ['watchedOnly']);
    } /* istanbul ignore next */ else if (value === PositionFilterEnum.ALL) {
      query = omit({ ...query, followedOnly: false, page: 1 }, ['watchedOnly']);
    } else if (value === PositionFilterEnum.WATCHED) {
      query = omit({ ...query, watchedOnly: true, page: 1 }, ['followedOnly']);
    } else if (value === PositionFilterEnum.FOLLOWED_WATCHED) {
      query = { ...query, followedOnly: true, watchedOnly: true, page: 1 };
    } else if (value === PositionFilterEnum.OPEN) {
      query = { ...query, positionStatus: 'open', followedOnly: true, watchedOnly: true, archive: false, page: 1 };
    } else if (value === PositionFilterEnum.ARCHIVED && isUserRestrictedInPositions) {
      query = { ...query, archive: true, positionStatus: 'closed', followedOnly: true, page: 1 };
      delete query.watchedOnly;
    } else if (value === PositionFilterEnum.ARCHIVED) {
      query = { ...query, archive: true, positionStatus: 'closed', followedOnly: false, page: 1 };
      delete query.watchedOnly;
    } else if (value === PositionFilterEnum.CLOSED && isUserRestrictedInPositions) {
      query = { ...query, positionStatus: 'closed', archive: false, followedOnly: true, page: 1 };
      delete query.watchedOnly;
    } else {
      query = { ...query, positionStatus: 'closed', archive: false, followedOnly: false, page: 1 };
      delete query.watchedOnly;
    }
    /* istanbul ignore next */
    if (activeBusiness) {
      setTimeout(() => {
        loadPositionList(activeBusiness, positionData, searchText, noOfUpdatedPosition, false, undefined, query).catch(
          /* istanbul ignore next */ (err) => console.log(err),
        );
      }, 0);
    }
    setNoOfUpdatedPosition(0);
  };

  const updatePositionByStatus = async (position: PositionFromPositionList) => {
    onUpdateStatusModalClose();
    /* istanbul ignore else */
    if (accountId && userId) {
      setUpdatingStatusId((ids) => ({
        ...ids,
        [position.id]: true,
      }));
      await updatePositionStatus({
        accountId,
        businessId: position.business,
        positionId: position.id,
        positionStatus: {
          open: !position.open,
        },
        // eslint-disable-next-line no-unneeded-ternary
        doCancellAppointmentHireOrOffer: position.open ? /* istanbul ignore next */ true : false,
      });

      const positionIndex = positionData.data.findIndex((posi) => posi.id === position.id);
      positionData.data[positionIndex].open = !position.open;

      /* istanbul ignore else */
      if (position.open) {
        positionData.data[positionIndex].assignedRecruiters = [{ id: userId, name: '' }];
        handleInfoBox(InfoType.OPEN);
      } else {
        handleInfoBox(InfoType.CLOSE);
      }

      positionData.data = positionData.data.filter((posi) => posi.id !== position.id);
      setNoOfUpdatedPosition(noOfUpdatedPosition + 1);

      /* istanbul ignore else */
      if (positionQueryParams.positionStatus === 'open') {
        setOpenPositionCountOfAllPosition(openPositionCountOfAllPosition - 1);
      } else {
        setOpenPositionCountOfAllPosition(openPositionCountOfAllPosition + 1);
      }

      setPositionData({ ...positionData });
      setUpdatingStatusId((ids) => ({
        ...ids,
        [position.id]: false,
      }));
    }
  };

  const updateArchiveStatus = async (position: PositionFromPositionList) => {
    onUpdateStatusModalClose();
    /* istanbul ignore else */
    if (accountId && userId) {
      setArchivingStatusId((ids) => ({
        ...ids,
        [position.id]: true,
      }));
      await updateArchivePosition({
        positionId: position.id,
      }).finally(() => setIsArchivingPosition(false));

      positionData.data = positionData.data.filter((posi) => posi.id !== position.id);
      setNoOfUpdatedPosition(noOfUpdatedPosition + 1);
      setPositionData({ ...positionData });
      setArchivingStatusId((ids) => ({
        ...ids,
        [position.id]: false,
      }));
    }

    handleInfoBox(InfoType.ARCHIVE);
    setIsArchivingPosition(false);
  };

  const updateUnArchiveStatus = async (position: PositionFromPositionList) => {
    onUpdateStatusModalClose();
    /* istanbul ignore else */
    if (accountId && userId) {
      setUnArchivingStatusId((ids) => ({
        ...ids,
        [position.id]: true,
      }));
      await updateUnArchivePosition({
        positionId: position.id,
      }).finally(() => setIsUnArchivingPosition(false));

      positionData.data = positionData.data.filter((posi) => posi.id !== position.id);
      setNoOfUpdatedPosition(noOfUpdatedPosition + 1);
      setPositionData({ ...positionData });
      setUnArchivingStatusId((ids) => ({
        ...ids,
        [position.id]: false,
      }));
    }
    handleInfoBox(InfoType.UNARCHIVE);
    setIsUnArchivingPosition(false);
  };

  const callArchiveHandler = async (position: PositionFromPositionList) => {
    if (isArchivingPosition) {
      await updateArchiveStatus(position);
    } else {
      await updateUnArchiveStatus(position);
    }
  };

  /* istanbul ignore next */
  const callbackAssignedRecruiters = (recruiters: FollowedRecruiters[], positionId: string) => {
    const positionIndex = positionData.data.findIndex((position) => position.id === positionId);
    positionData.data[positionIndex].assignedRecruiters = recruiters;
    positionData.data[positionIndex].watchedRecruiters = recruiters.filter((r) => r.id !== userId);

    /* istanbul ignore next */
    if (recruiters.length === 0) {
      positionData.data[positionIndex].open = false;
    }

    /* istanbul ignore next */
    if (positionQueryParams.followedOnly || recruiters.length === 0) {
      positionData.data = positionData.data.filter((position) => position.id !== positionId);
      setNoOfUpdatedPosition(noOfUpdatedPosition + 1);
    }

    setPositionData({ ...positionData });
  };

  /* istanbul ignore next */
  const callbackWatchedRecruiters = (recruiters: FollowedRecruiters[], positionId: string) => {
    const positionIndex = positionData.data.findIndex((position) => position.id === positionId);
    positionData.data[positionIndex].watchedRecruiters = recruiters;
    positionData.data[positionIndex].assignedRecruiters = recruiters.filter((r) => r.id !== userId);

    /* istanbul ignore next */
    if (positionData?.data?.[positionIndex]?.assignedRecruiters?.length === 0) {
      positionData.data[positionIndex].open = false;
    }

    /* istanbul ignore next */
    if (positionQueryParams.watchedOnly || recruiters.length === 0) {
      positionData.data = positionData.data.filter((position) => position.id !== positionId);
      setNoOfUpdatedPosition(noOfUpdatedPosition + 1);
    }

    setPositionData({ ...positionData });
  };

  // istanbul ignore next
  const updatePositionAutomation = (
    givenPositionId: string,
    givenAutomationType: string | null,
    screeningTarget: number | undefined,
    shouldUpdateAutomationApiCalled?: boolean,
    schedulingVrEnabled?: boolean,
  ): Promise<boolean> => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises,no-async-promise-executor
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        if (shouldUpdateAutomationApiCalled) {
          await setPositionAutomation({
            positionId: givenPositionId,
            automationType: givenAutomationType as AutomationTypes,
            virtualRecruiterEnabled: schedulingVrEnabled,
            candidateScreenTarget: screeningTarget,
          });
        }

        const updatedPositionListData = positionData.data.map((positionItem) =>
          givenPositionId === positionItem.id
            ? {
                ...positionItem,
                automationType: givenAutomationType,
                ...(givenAutomationType === AutomationTypes.SCREENING &&
                  screeningTarget !== undefined && {
                    automationConfiguration: {
                      target: screeningTarget,
                    },
                  }),
                virtualRecruiterEnabled: givenAutomationType === AutomationTypes.SCHEDULING && schedulingVrEnabled,
              }
            : positionItem,
        );

        setPositionData({ ...positionData, data: updatedPositionListData });

        // Manually resolve the promise if successful
        resolve(true);
      } catch (error) {
        // Manually reject the promise if an error occurs
        reject(error);
      }
    });
  };

  const onChangeSearchText = (value: string) => {
    setSearchText(value);
  };

  const disablePositionCreation = !_isNil(authorization) && !authorization;

  const loadRecruiterImage = (recruiter: FollowedRecruiters) => {
    return (
      <Tooltip label={recruiter.name} shouldWrapChildren placement="top" key={recruiter.id}>
        <ProfilePicture
          src={recuriterImages.get(recruiter.id)}
          style={{ backgroundColor: colors.persianBlue, fontWeight: 600, color: '#fff', marginRight: '-10px' }}
          name={recruiter.name}
          showBorder
          bg="transparent"
          size="sm"
        />
      </Tooltip>
    );
  };

  const loadFollowedRecruiterListItems = (recruiter: FollowedRecruiters) => {
    return (
      <Flex alignItems="center" py={3}>
        <ProfilePicture
          src={recuriterImages.get(recruiter.id)}
          style={{ backgroundColor: 'blue', color: colors.white }}
          name={recruiter.name}
          bg="transparent"
          size="sm"
        />
        <Box px={3}>
          <Text>{recruiter.name}</Text>
        </Box>
      </Flex>
    );
  };
  const { isOpen, onClose, onOpen } = useDisclosure();
  const messageForOpenPosition = (position: PositionFromPositionList) => {
    if (!_isNil(automationEnabled) && automationEnabled && position.automated) {
      switch (position.automationType) {
        case 'screening':
          return (
            <Box>
              <Text>{t('jobPosition:openPositionModal.subtitle')}</Text>
              <Heading as="h4" fontSize="lg" mt={1} mb={1}>
                {t('jobPosition:openPositionModal.screening.title')}
              </Heading>
              <Text>
                {position.automationConfiguration?.target === 1
                  ? t('jobPosition:openPositionModal.screening.description.one', { count: 1 })
                  : t('jobPosition:openPositionModal.screening.description.other', {
                      count: position.automationConfiguration?.target,
                    })}
              </Text>
            </Box>
          );
        case 'scheduling':
          return (
            <Box>
              <Text>{t('jobPosition:openPositionModal.subtitle')}</Text>
              <Heading as="h4" fontSize="lg" mt={1} mb={1}>
                {t('jobPosition:openPositionModal.scheduling.title')}
              </Heading>
              <Text>{t('jobPosition:openPositionModal.scheduling.description')}</Text>
            </Box>
          );
        default:
          return (
            <Box>
              <Text>{t('jobPosition:openPositionModal.subtitle')}</Text>
              <Heading as="h4" fontSize="lg" mt={1} mb={1}>
                {t('jobPosition:openPositionModal.disabled.title')}
              </Heading>
              <Text>{t('jobPosition:openPositionModal.disabled.description')}</Text>
            </Box>
          );
      }
    } else {
      //  istanbul ignore next
      return <Text>{disablePositionCreation ? '' : t('jobPosition:openPositionModal.noAutomation.description')}</Text>;
    }
  };

  const messageForClosePosition = (noOfRecruiter: number | undefined) => {
    if (!_isNil(noOfRecruiter)) {
      if (noOfRecruiter - 1 < 1) {
        return t('jobPosition:closePositionModal.subtitle.zero');
      }
      if (noOfRecruiter - 1 === 1) {
        return t('jobPosition:closePositionModal.subtitle.one', { count: noOfRecruiter - 1 });
      }
      return t('jobPosition:closePositionModal.subtitle.other', { count: noOfRecruiter - 1 });
    }
    return '';
  };

  const confirmUpdateByStatusHeaderMessage = (isPositionOpen: boolean) => {
    if (isRequisitionEnabled) {
      return t('jobPosition:openPositionModal.requisition.title');
    }
    return isPositionOpen ? t('jobPosition:closePositionModal.title') : t('jobPosition:openPositionModal.title');
  };

  const confirmUpdateByStatusBodyMessage = (position: PositionFromPositionList) => {
    if (isRequisitionEnabled) {
      return !position.open
        ? /* istanbul ignore next */ t('jobPosition:closePositionModal.requisition.subtitle')
        : `${t('jobPosition:openPositionModal.requisition.subtitle')}`;
    }

    return position.open ? `${messageForClosePosition(position.assignedRecruiters?.length)}` : messageForOpenPosition(position);
  };

  const confirmUpdateByStatus = (position: PositionFromPositionList) => {
    const confirmMessage = {
      headerMessage: confirmUpdateByStatusHeaderMessage(position.open),
      bodyMessage: confirmUpdateByStatusBodyMessage(position),
      confirmText: position.open
        ? t('jobPosition:closePositionModal.closePositionButton.title')
        : t('jobPosition:openPositionModal.openPositionButton'),
      cancelText: t('jobPosition:openPositionModal.cancelModalButton'),
    } as ConfirmMessageType;
    setConfirmUpdateStatusMessage(confirmMessage);
    setSelectedPosition(position);
    onUpdateStatusModalOpen();
  };

  const confirmArchivePosition = (position: PositionFromPositionList) => {
    setIsArchivingPosition(true);
    const confirmMessage = {
      headerMessage: t('jobPosition:archivePositionModal.title'),
      bodyMessage: t('jobPosition:archivePositionModal.bodyMessage'),
      confirmText: t('jobPosition:archivePositionModal.confirmText'),
      cancelText: t('jobPosition:archivePositionModal.cancelText'),
    } as ConfirmMessageType;
    setConfirmUpdateStatusMessage(confirmMessage);
    setSelectedPosition(position);
    onUpdateStatusModalOpen();
  };

  const confirmUnArchivePosition = (position: PositionFromPositionList) => {
    setIsUnArchivingPosition(true);
    const confirmMessage = {
      headerMessage: t('jobPosition:unarchivePositionModal.title'),
      bodyMessage: t('jobPosition:unarchivePositionModal.bodyMessage'),
      confirmText: t('jobPosition:unarchivePositionModal.confirmText'),
      cancelText: t('jobPosition:unarchivePositionModal.cancelText'),
    } as ConfirmMessageType;
    setConfirmUpdateStatusMessage(confirmMessage);
    setSelectedPosition(position);
    onUpdateStatusModalOpen();
  };

  const handlePositionToEdit = async (res: PositionFromPositionList) => {
    const response = await getPositionDetail({
      accountId: res.account,
      businessId: res.business,
      positionId: res.id,
    });
    setPositionToEdit(response);
    setCreateNewPosition(true);
  };

  const copyDeepLinkOfPosition = (positionId: string) => {
    onPositionLinkOpen();
    setPositionLinkOpen(true);
    setCurrentPositionId(positionId);
  };

  /* istanbul ignore next */
  const getHiredAndOpenignsData = (position: PositionFromPositionList) => {
    let hiredCount = 0;
    let openings: string | number = '-';
    if (position?.noOfHiredCandidates) {
      hiredCount = position.noOfHiredCandidates;
    }
    if (position?.noOfOpenings) {
      openings = position.noOfOpenings;
    }
    return `${hiredCount} / ${openings}`;
  };

  const loadAutomationInfo = (positionInformation: PositionFromPositionList) => {
    // istanbul ignore next
    if (positionInformation.automationType === AutomationTypes.SCHEDULING && positionInformation?.virtualRecruiterEnabled) {
      return (
        <PositionAutomationInfoView
          toolTipMessage={t('jobPosition:automation.schedulingVrEnabled.toolTipMessage')}
          automationLabel={t('jobPosition:automation.schedulingVrEnabled.label')}
        />
      );
    }
    switch (positionInformation.automationType) {
      case AutomationTypes.SCREENING:
        return (
          <PositionAutomationInfoView
            toolTipMessage={t('jobPosition:automation.screening.toolTipMessage', {
              count: positionInformation.automationConfiguration?.target,
            })}
            automationLabel={t('jobPosition:automation.screening.label', {
              count: positionInformation.automationConfiguration?.target,
            })}
          />
        );
      case AutomationTypes.SCHEDULING:
        return (
          <PositionAutomationInfoView
            toolTipMessage={t('jobPosition:automation.scheduling.toolTipMessage')}
            automationLabel={t('jobPosition:automation.scheduling.label')}
          />
        );
      default:
        return (
          <PositionAutomationInfoView
            toolTipMessage={t('jobPosition:automation.findMeCandidate.toolTipMessage')}
            automationLabel={t('jobPosition:automation.findMeCandidate.label')}
          />
        );
    }
  };

  const renderShareButton = (positionId: string) => (
    <Button
      leftIcon={<RiShareFill />}
      size="sm"
      background={colors.input.background}
      transition="all 0.2s"
      borderRadius="md"
      border="none"
      px={5}
      mr={2}
      data-testid={`copyDeepLinkButton-${positionId}`}
      _hover={{ bg: colors.lavenderMist }}
      _expanded={{ bg: colors.blue.default, color: '#fff' }}
      onClick={() => copyDeepLinkOfPosition(positionId)}
    >
      {t('jobPosition:share')}
    </Button>
  );

  const renderPositionJobType = (position: PositionFromPositionList) => {
    // istanbul ignore next
    if (position?.positionType) {
      return t(`jobPosition:${position?.positionType}`);
    }
    return position.fullTime ? t('jobPosition:fullTime') : /* istanbul ignore next */ t('jobPosition:partTime');
  };

  const loadPositionItem = (position: PositionFromPositionList, index: number) => {
    const isPositionFollowed =
      position.assignedRecruiters &&
      position?.assignedRecruiters?.filter((value) => {
        return value.id === userId;
      }).length > 0;
    const isPositionWatched =
      (position?.watchedRecruiters &&
        /* istanbul ignore next */ position.watchedRecruiters?.some(/* istanbul ignore next */ (value) => value.id === userId)) ??
      /* istanbul ignore next */ false;

    const validAssignedRecruiters = position.assignedRecruiters?.filter((r) => !ghostedRecruiterIds.has(r.id)) || [];

    const currentAutomationValue = _isNil(position?.automationType) ? 'null' : position.automationType;

    return (
      <Flex
        px={6}
        py={4}
        direction="column"
        key={position.id}
        bg={
          // eslint-disable-next-line no-nested-ternary
          isRequisitionEnabled && position.open
            ? !_isNil(position.isPaused) && !position.isPaused
              ? colors.white
              : 'yellow.50'
            : colors.white
        }
      >
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <VStack align="flex-start" spacing={0}>
            {position.positionId && (
              <Heading
                as="h3"
                fontSize="13px"
                fontWeight={600}
                lineHeight={1.4}
                color="gray.500"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {t('createJobPosition:positionId')}: {position.positionId}
              </Heading>
            )}

            <HStack align="center">
              <Text as="h5" fontSize="md" display="flex" fontWeight="800">
                {`${index + 1}. `}

                {(isPositionFollowed || isPositionWatched) && position.open ? (
                  // <Link
                  //   to={{ pathname: `/positionCandidates/${position.id}/messaging`, state: 'PositionList' }}
                  //   style={{ color: colors.linkBlue, textDecoration: 'underline' }}
                  // >
                  //   {position.customName ? position.customName : /* istanbul ignore next */ position.jobTitle}
                  // </Link>

                  <Box color={colors.linkBlue} ml={1}>
                    {position.customName ? position.customName : /* istanbul ignore next */ position.jobTitle}
                  </Box>
                ) : (
                  <span>{position.customName ? position.customName : /* istanbul ignore next */ position.jobTitle}</span>
                )}
              </Text>
              <Box>
                {isPositionFollowed && (
                  <Tooltip label={t('jobPosition:followedPositions')} hasArrow>
                    <Box>
                      <MdPersonAddAlt1 />
                    </Box>
                  </Tooltip>
                )}
                {!isPositionFollowed && isPositionWatched && (
                  // istanbul ignore next
                  <Tooltip label={t('businessManagement:businessTypeFilter:watched')} hasArrow>
                    <Box data-testid={`watchedIndicator-${position.id}`}>
                      <IoEye />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </HStack>
          </VStack>
          <Flex>
            {(isPositionFollowed || isPositionWatched) && position.open && (
              <>
                <Button
                  leftIcon={<IoMdChatbubbles size={18} />}
                  background={colors.blue.default}
                  color={colors.white}
                  mr={2}
                  px={4}
                  height="36px"
                  borderRadius={5}
                  cursor="pointer"
                  fontSize="14px"
                  boxShadow="sm"
                  colorScheme="blue"
                  onClick={
                    /* istanbul ignore next */ () => {
                      history.push(`/positionCandidates/${position.id}/messaging`, {
                        state: 'PositionList',
                        locationToPushOnBack: '/administration/positionManagement',
                      });
                    }
                  }
                >
                  {t('jobPosition:messaging')}
                </Button>

                <Button
                  leftIcon={<RiUserStarLine size={18} fontSize={700} />}
                  background={colors.blue.default}
                  color={colors.white}
                  mr={2}
                  px={4}
                  height="36px"
                  borderRadius={5}
                  cursor="pointer"
                  fontSize="14px"
                  boxShadow="sm"
                  colorScheme="blue"
                  onClick={
                    /* istanbul ignore next */ () => {
                      history.push(
                        {
                          pathname: PATH_POSITION_TABS(position.id, SelectedTab.BEST_MATCH),
                        },
                        {
                          fromPage: location.pathname,
                        },
                      );
                    }
                  }
                >
                  {t('jobPosition:topCandidates')}
                </Button>
              </>
            )}

            {!position.open && renderShareButton(position.id)}

            {/* {isAdministrationAccount && !isCurrentPage('/positions') && !positionQueryParams.archive && (
            <Tooltip
              label={`${t('jobPosition:userRestrictedByAuthorizationMessageFront')} ${contactEmailForAccountAuthorizaton} ${t(
                'jobPosition:userRestrictedByAuthorizationMessageBack',
              )}`}
              shouldWrapChildren
              isDisabled={!disablePositionCreation}
            >
              <Box height="fit-content">
                <IconButton
                  size="sm"
                  fontSize="1.3rem"
                  colorScheme="blue"
                  aria-label={t('jobPosition:edit')}
                  icon={<RiEditBoxLine />}
                  mr={3}
                  data-testid={`PositionEditBtn-${position.id}`}
                  onClick={() => {
                    handlePositionToEdit(position);
                  }}
                  disabled={disablePositionCreation}
                />
              </Box>
            </Tooltip>
          )} */}

            {!position.archived && (
              <Menu>
                <MenuButton
                  height={position.open ? '36px' : '32px'}
                  px={4}
                  py={2}
                  background={colors.input.background}
                  transition="all 0.2s"
                  borderRadius="md"
                  borderWidth="1px"
                  border="none"
                  _hover={{ bg: colors.lavenderMist }}
                  _expanded={{ bg: colors.blue.default, color: '#fff' }}
                  data-testid={`MenuButton-${position.id}`}
                >
                  <Box display="flex" alignItems="center" fontWeight="600">
                    {isUpdatingFollow[position.id] ||
                    isUpdatingStatusId[position.id] ||
                    isArchivingStatusId[position.id] ||
                    isUnArchivingStatusId[position.id] ? (
                      <Spinner size="sm" display="flex" />
                    ) : (
                      <BsThreeDotsVertical />
                    )}
                  </Box>
                </MenuButton>
                <MenuList minWidth="8rem">
                  {userId &&
                    !positionQueryParams.archive &&
                    (!isUserRestrictedInPositions || /* istanbul ignore next */ !isPositionFollowed) && (
                      <>
                        <MenuItem p={0}>
                          <PositionFollowButton
                            recruiterId={userId}
                            assignedRecruiters={position.assignedRecruiters ? position.assignedRecruiters : []}
                            positionId={position.id}
                            callbackAssignedRecruiters={callbackAssignedRecruiters}
                            notifyUpdatingStateToParent={getIsUpdatingFollowFromChild}
                          />
                        </MenuItem>
                      </>
                    )}
                  {
                    // TODO: commented for next release
                  }
                  {userId &&
                    !positionQueryParams.archive &&
                    (!isUserRestrictedInPositions || /* istanbul ignore next */ !isPositionWatched) && (
                      <>
                        <MenuItem p={0}>
                          <PositionWatchButton
                            recruiterId={userId}
                            watchedRecruiters={position.watchedRecruiters || /* istanbul ignore next */ []}
                            followedRecruiters={position.assignedRecruiters || []}
                            positionId={position.id}
                            callbackWatchedRecruiters={callbackWatchedRecruiters}
                            notifyUpdatingStateToParent={getIsUpdatingFollowFromChild}
                          />
                        </MenuItem>
                      </>
                    )}
                  <>
                    <MenuItem p={0}>
                      <Button
                        size="sm"
                        variant="ghost"
                        width="100%"
                        borderRadius="unset"
                        isLoading={isUpdatingStatusId[position.id]}
                        data-testid={`UpdateStatusBtn-${position.id}`}
                        onClick={() => confirmUpdateByStatus(position)}
                        // onClick={() => updatePositionByStatus(position)}
                      >
                        {position.open ? t('jobPosition:close') : t('jobPosition:open')}
                      </Button>
                    </MenuItem>
                  </>
                  {!position.open && !position.archived && isAdministrationAccount && (
                    <>
                      <MenuItem p={0}>
                        <Button
                          size="sm"
                          variant="ghost"
                          width="100%"
                          borderRadius="unset"
                          isLoading={isArchivingStatusId[position.id]}
                          data-testid={`ArchiveBtn-${position.id}`}
                          onClick={() => confirmArchivePosition(position)}
                          // onClick={() => updatePositionByStatus(position)}
                        >
                          {t('jobPosition:archive')}
                        </Button>
                      </MenuItem>
                    </>
                  )}
                  {enableCopyPosition && isAdministrationAccount && (
                    <>
                      <Tooltip
                        label={`${t(
                          'jobPosition:userRestrictedByAuthorizationMessageFront',
                        )} ${contactEmailForAccountAuthorizaton} ${t('jobPosition:userRestrictedByAuthorizationMessageBack')}`}
                        shouldWrapChildren
                        isDisabled={!disablePositionCreation}
                      >
                        <MenuItem p={0}>
                          <Button
                            size="sm"
                            variant="ghost"
                            width="100%"
                            style={{ borderRadius: 'unset', background: 'unset' }}
                            onClick={() => {
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                              handlePositionToEdit(position);
                              setShowCopyPosition();
                            }}
                            data-testid={`copyPositionBtn-${position.id}`}
                            disabled={disablePositionCreation}
                          >
                            {t('jobPosition:copy')}
                          </Button>
                        </MenuItem>
                      </Tooltip>
                    </>
                  )}
                  {isAdministrationAccount && !isCurrentPage('/positions') && !positionQueryParams.archive && (
                    <Tooltip
                      label={`${t(
                        'jobPosition:userRestrictedByAuthorizationMessageFront',
                      )} ${contactEmailForAccountAuthorizaton} ${t('jobPosition:userRestrictedByAuthorizationMessageBack')}`}
                      shouldWrapChildren
                      isDisabled={!disablePositionCreation}
                    >
                      {/* <Box height="fit-content">
                      <IconButton
                        size="xs"
                        fontSize="1rem"
                        colorScheme="blue"
                        aria-label={t('jobPosition:edit')}
                        icon={<RiEditBoxLine />}
                        ml={3}
                        data-testid={`PositionEditBtn-${position.id}`}
                        onClick={() => {
                          handlePositionToEdit(position);
                        }}
                        disabled={disablePositionCreation}
                      />
                    </Box> */}
                      <MenuItem p={0}>
                        <Button
                          size="sm"
                          variant="ghost"
                          width="100%"
                          style={{ borderRadius: 'unset', background: 'unset' }}
                          onClick={() => {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            handlePositionToEdit(position);
                          }}
                          data-testid={`PositionEditBtn-${position.id}`}
                          disabled={disablePositionCreation}
                        >
                          {t('jobPosition:edit')}
                        </Button>
                      </MenuItem>
                    </Tooltip>
                  )}
                </MenuList>
              </Menu>
            )}

            {position.archived && !position.open && isAdministrationAccount && (
              <Box>
                <Button
                  leftIcon={<RiInboxUnarchiveLine />}
                  ml={3}
                  size="sm"
                  width="115px"
                  isLoading={isUnArchivingStatusId[position.id]}
                  colorScheme="blue"
                  data-testid={`unArchiveBtn-${position.id}`}
                  onClick={() => confirmUnArchivePosition(position)}
                >
                  {t('jobPosition:unArchive')}
                </Button>
              </Box>
            )}
          </Flex>
        </Flex>

        <Grid templateColumns={{ base: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }} gap={1}>
          <GridItem mr={2}>
            <Flex direction="column">
              <Flex alignItems="flex-start" mr={8} mb={6}>
                <AiFillClockCircle size={21} style={{ marginRight: '10px' }} color={colors.gray[400]} />
                <Box>
                  <Heading
                    as="h6"
                    fontSize="14px"
                    fontWeight={400}
                    lineHeight="1"
                    color={colors.gray[500]}
                    whiteSpace="nowrap"
                    mb={2}
                  >
                    {t('jobPosition:time')}
                  </Heading>
                  <Text fontSize="14px" fontWeight="bold">
                    {renderPositionJobType(position)}
                  </Text>
                </Box>
              </Flex>
            </Flex>

            <Flex alignItems="normal" width="80%">
              {(isPositionFollowed || isPositionWatched) && !position.archived && (
                <Flex direction="column">
                  <Heading as="h6" fontSize="14px" fontWeight={400} lineHeight="1" color={colors.gray[500]} mb={2}>
                    {t('jobPosition:automationTypes.selectAutomationLevel')}
                  </Heading>

                  <PositionAutomationSelect
                    positionId={position.id}
                    currentAutomationValue={currentAutomationValue as AutomationTypes}
                    isAccountVirtualRecruiterEnabled={isAccountVirtualRecruiterEnabled}
                    updatePositionAutomation={updatePositionAutomation}
                    automationConfiguration={position.automationConfiguration}
                    isPositionVirtualRecruiterEnabled={position.virtualRecruiterEnabled}
                  />
                </Flex>
              )}
            </Flex>
          </GridItem>

          <GridItem>
            <Flex direction="column">
              {/* <Flex alignItems="flex-start" mr={8} mb={6}>
                <MdRecordVoiceOver size={21} style={{ marginRight: '10px' }} color={colors.gray[400]} />
                <Box>
                  <Heading
                    as="h6"
                    fontSize="14px"
                    fontWeight={400}
                    lineHeight="1"
                    color={colors.gray[500]}
                    whiteSpace="nowrap"
                    mb={2}
                  >
                    {t('jobPosition:applicants')}
                  </Heading>
                  <Text fontSize="14px" fontWeight="bold">
                    { position.applicants ? position.applicants : ' -'}
                  </Text>
                </Box>
              </Flex> */}

              <Flex alignItems="center" mr={8} height="100%" width="75%">
                <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                  <Heading
                    as="h6"
                    fontSize="14px"
                    fontWeight={400}
                    lineHeight="1"
                    color={colors.gray[500]}
                    whiteSpace="nowrap"
                    mb={2}
                  >
                    {t('jobPosition:automation.title')}
                  </Heading>
                  <Text fontSize="14px" fontWeight="bold">
                    {loadAutomationInfo(position)}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </GridItem>

          <GridItem>
            <Flex direction="column">
              <Flex alignItems="flex-start" mb={6}>
                <MdPerson size={21} style={{ marginRight: '10px' }} color={colors.gray[400]} />
                <Box>
                  <Heading
                    as="h6"
                    fontSize="14px"
                    fontWeight={400}
                    lineHeight="1"
                    color={colors.gray[500]}
                    whiteSpace="nowrap"
                    mb={2}
                  >
                    {t('jobPosition:noOfHires')}
                  </Heading>
                  <Text fontSize="14px" fontWeight="bold">
                    {getHiredAndOpenignsData(position)}
                    {/* {`${position.noOfHiredCandidates ? position.noOfHiredCandidates : '0 '}/${
                    position.noOfOpenings ? position.noOfOpenings : ' -'
                  }`} */}
                  </Text>
                </Box>
              </Flex>

              {validAssignedRecruiters && validAssignedRecruiters.length > 0 ? (
                <Flex alignItems="flex-start">
                  <MdPersonAddAlt1 size={21} style={{ marginRight: '10px' }} color={colors.gray[400]} />
                  <Box>
                    <Heading
                      as="h6"
                      fontSize="14px"
                      fontWeight={400}
                      lineHeight="1"
                      color={colors.gray[500]}
                      whiteSpace="nowrap"
                      mb={2}
                    >
                      {t('jobPosition:recruiterFollowing')}
                    </Heading>

                    <HStack
                      onClick={() => {
                        setAssignedRecruiters(validAssignedRecruiters);
                        onOpen();
                      }}
                      style={{ cursor: 'pointer' }}
                      spacing={4}
                      data-testid="showAllRecuriter"
                    >
                      <AvatarGroup size="xs" max={5}>
                        {validAssignedRecruiters.slice(0, 5).map((item: FollowedRecruiters) => loadRecruiterImage(item))}
                      </AvatarGroup>
                      {validAssignedRecruiters.length > 5 && (
                        <Text fontSize="sm" color="gray.600">
                          {`+${validAssignedRecruiters.length - 5}`}
                        </Text>
                      )}
                    </HStack>
                  </Box>
                </Flex>
              ) : (
                ''
              )}
            </Flex>
          </GridItem>
          <Flex justifyContent="flex-end" alignItems="center" mr={14}>
            {isRequisitionEnabled && position.open && (
              <PositionRequisitionMenuButton position={position} isLastItem={positionData.data.length - 1 === index} />
            )}
            {position.open && renderShareButton(position.id)}
          </Flex>
        </Grid>
      </Flex>
    );
  };

  const onRefreshClick = () => {
    filterJobPositionList(
      positionQueryParams.archive
        ? /* istanbul ignore next */ PositionFilterEnum.ARCHIVED
        : (positionQueryParams.positionStatus as PositionFilterEnum),
    );
  };

  useEffect(() => {
    /* istanbul ignore next */
    if (activeBusiness) {
      loadPositionList(activeBusiness, positionData, searchText, noOfUpdatedPosition, false, filterType).catch((err) =>
        console.log(err),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusiness]);

  useEffect(() => {
    const isCurrentlyInClosedPositionTab = positionQueryParams.positionStatus === 'closed' && !positionQueryParams.archive;

    if (refreshPositionPageNotificationCode === NotificationTypes.ES_POSITION_CREATED && !isCurrentlyInClosedPositionTab) {
      clearRefreshPositionPageNotification();
      return;
    }

    if (refreshPositionPageNotificationCode === NotificationTypes.ES_POSITION_CREATED && isCurrentlyInClosedPositionTab) {
      /* istanbul ignore next */
      if (activeBusiness) {
        onRefreshClick();
      }
      clearRefreshPositionPageNotification();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPositionPageNotificationCode]);

  const handlePositionForm = () => {
    setCreateNewPosition(true);
  };

  const redirectToPositionAfterCreated = (jobPositionData: JobPosition) => {
    const updatedPositionListData = positionData.data.map((positionItem) =>
      /* istanbul ignore next */ positionItem.id === positionToEdit?.id
        ? {
            ...positionItem,
            jobCustomName: jobPositionData.jobCustomName,
            customName: jobPositionData.jobCustomName ?? '',
            fullTime: jobPositionData.fullTime,
            automationType: jobPositionData.automationType,
            ...(jobPositionData.automationType === AutomationTypes.SCREENING &&
              !_isNil(jobPositionData.automationConfiguration?.target) &&
              isNumber(jobPositionData.automationConfiguration?.target) && {
                automationConfiguration: {
                  target: jobPositionData.automationConfiguration?.target as number,
                },
              }),
            virtualRecruiterEnabled:
              jobPositionData.automationType === AutomationTypes.SCHEDULING && jobPositionData.virtualRecruiterEnabled,
          }
        : positionItem,
    );

    setPositionData({ ...positionData, data: updatedPositionListData });

    setCreateNewPosition(false);
    setPositionToEdit(null);
  };

  if (createNewPosition && !enableNewUiForCreatePosition) {
    return (
      <Box ref={positionRef}>
        <CreateJobsView
          positionDataToEdit={positionToEdit}
          redirectToPositionAfterCreated={redirectToPositionAfterCreated}
          selectedBusiness={activeBusiness}
          setCreateNewPosition={(createPosition) => setCreateNewPosition(createPosition)}
          updatePositionAutomation={updatePositionAutomation}
        />
      </Box>
    );
  }

  return (
    <Box ref={positionRef}>
      {positionLinkOpen && (
        <PositionLinkModal isOpen={isPositionLinkOpen} onClose={onPositionLinkClosed} positionId={currentPositionId} />
      )}
      {!_isNil(activeBusiness) && (
        <Fragment>
          <Box backgroundColor={colors.white} borderRadius={5} mb={6} px={6} py={4}>
            <Stack spacing={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  data-testid="searchInput"
                  type="text"
                  value={searchText}
                  placeholder={t('jobPosition:searchPlaceholder')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeSearchText(e.target.value)}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    e.key === 'Enter' && loadPositionList(activeBusiness, positionData, searchText, noOfUpdatedPosition, false)
                  }
                />
              </InputGroup>
            </Stack>
          </Box>
          <Flex
            bg="#fff"
            px={4}
            py={3}
            alignItems="center"
            color="gray.500"
            borderBottom="1px solid #eee"
            borderRadius="5px 5px 0 0"
          >
            <Flex alignItems="center" w="100%">
              <Button
                onClick={() => {
                  setScrollToBusiness({ ...activeBusiness, positions: positionData.count });
                  changeActiveBusiness(null);
                }}
                variant="ghost"
                colorScheme="blue"
                style={{ color: colors.blue[500] }}
                px={2}
                data-testid="backToBusiness"
                leftIcon={<MdArrowBack size={20} />}
              >
                {t('createJobPosition:stepsNav.back')}
              </Button>

              <Spacer />
              <Flex alignItems="center">
                <Button
                  onClick={onRefreshClick}
                  size="md"
                  fontSize="sm"
                  colorScheme="gray"
                  leftIcon={<IoMdRefresh size={20} />}
                  data-testid="refreshBtn"
                  mr={4}
                  variant="outline"
                >
                  {t('jobPosition:refresh')}
                </Button>
                <Box css={selectStyle}>
                  <Select
                    onChange={(e) => filterJobPositionList(e.target.value as PositionFilterEnum)}
                    bg="blue.500"
                    color="#fff"
                    borderColor="blue.500"
                    maxW="220px"
                    mr={5}
                    fontSize="sm"
                    fontWeight="bold"
                    data-testid="filterPositionBtn"
                    value={selectedFilteryType}
                  >
                    {!isUserRestrictedInPositions && (
                      <option value={PositionFilterEnum.ALL} style={{ color: '#000' }}>
                        {t('jobPosition:allPositions')}
                      </option>
                    )}
                    <option value={PositionFilterEnum.FOLLOWED} style={{ color: '#000' }}>
                      {t('jobPosition:followedPositions')}
                    </option>
                    <option value={PositionFilterEnum.WATCHED} style={{ color: '#000' }}>
                      {t('businessManagement:businessTypeFilter:watched')}
                    </option>
                    <option value={PositionFilterEnum.FOLLOWED_WATCHED} style={{ color: '#000' }}>
                      {t('businessManagement:businessTypeFilter:followAndWatch')}
                    </option>
                  </Select>
                </Box>
                <Box>
                  <ButtonGroup isAttached backgroundColor="white" borderRadius={5}>
                    <Popover
                      isOpen={showPositionOpenInfo}
                      onClose={setShowPositionOpenInfoClose}
                      placement="top"
                      closeOnBlur={false}
                    >
                      <PopoverTrigger>
                        <Button
                          onClick={() => filterJobPositionList(PositionFilterEnum.OPEN)}
                          size="md"
                          fontSize="sm"
                          colorScheme={colors.blue[40]}
                          bg={positionQueryParams.positionStatus === 'open' ? colors.blue[20] : colors.white}
                          borderColor={colors.neutral[200]}
                          color={positionQueryParams.positionStatus === 'open' ? colors.blue[500] : colors.gray[500]}
                          data-testid="openPositionBtn"
                          variant="outline"
                        >
                          {t('jobPosition:openPositions')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopoverArrow background={colors.blue.default} />
                        <PopoverCloseButton color={colors.white} />
                        <PopoverBody background={colors.blue.default} color={colors.white}>
                          {popOverMessage}
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                    <Popover
                      isOpen={showPositionCloseInfo}
                      onClose={setShowPositionCloseInfoClose}
                      placement="top"
                      closeOnBlur={false}
                    >
                      <PopoverTrigger>
                        <Button
                          onClick={() => filterJobPositionList(PositionFilterEnum.CLOSED)}
                          size="md"
                          fontSize="sm"
                          colorScheme={colors.blue[40]}
                          bg={
                            positionQueryParams.positionStatus === 'closed' && !positionQueryParams.archive
                              ? colors.blue[20]
                              : colors.white
                          }
                          borderColor={colors.neutral[200]}
                          color={
                            positionQueryParams.positionStatus === 'closed' && !positionQueryParams.archive
                              ? colors.blue[500]
                              : colors.gray[500]
                          }
                          data-testid="closePositionBtn"
                          variant="outline"
                        >
                          {t('jobPosition:closedPositions')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopoverArrow background={colors.blue.default} />
                        <PopoverCloseButton color={colors.white} />
                        <PopoverBody background={colors.blue.default} color={colors.white}>
                          {popOverMessage}
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>

                    <Popover
                      isOpen={showPositionArchievedInfo}
                      onClose={setShowPositionArchievedInfoClose}
                      placement="top"
                      closeOnBlur={false}
                    >
                      <PopoverTrigger>
                        <Button
                          onClick={() => filterJobPositionList(PositionFilterEnum.ARCHIVED)}
                          size="md"
                          fontSize="sm"
                          colorScheme={colors.blue[40]}
                          bg={positionQueryParams.archive ? /* istanbul ignore next */ colors.blue[20] : colors.white}
                          borderColor={colors.neutral[200]}
                          color={positionQueryParams.archive ? /* istanbul ignore next */ colors.blue[500] : colors.gray[500]}
                          data-testid="archivedPositionBtn"
                          variant="outline"
                        >
                          {t('jobPosition:archivedPositions')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopoverArrow background={colors.blue.default} />
                        <PopoverCloseButton color={colors.white} />
                        <PopoverBody background={colors.blue.default} color={colors.white}>
                          {t('jobPosition:info.archived')}
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </ButtonGroup>
                </Box>
              </Flex>
            </Flex>
          </Flex>
          <Accordion defaultIndex={[0]}>
            <AccordionItem backgroundColor="#fff" mb={4} border={0}>
              <h2>
                <AccordionButton as="div" px={6} py={4} borderBottom="1px solid #eceff1">
                  <Flex flex="1" textAlign="left" justifyContent="space-between" alignItems="center">
                    <Heading as="h4" fontSize="lg">
                      {activeBusiness?.name}
                    </Heading>
                    <Flex alignItems="center">
                      {isRequisitionEnabled && (
                        <FormControl w="auto" display="flex" alignItems="center" mr={3}>
                          <FormLabel fontSize="sm" htmlFor="filterByRequisitions" mb="0">
                            {t('jobPosition:filterByRequisitions')}
                          </FormLabel>
                          <Switch
                            size="sm"
                            id="filterByRequisitions"
                            data-testid="filterByRequisitions"
                            onChange={(e) => setFilterByRequisition(e.target.checked)}
                          />
                        </FormControl>
                      )}
                      <Button
                        color="blue.500"
                        fontWeight="bold"
                        isLoading={isLoading}
                        loadingText={t('jobPosition:counting')}
                        border="0"
                        backgroundColor="#F3F3F3"
                        _hover={{ bg: '#F3F3F3' }}
                        fontSize=".9rem"
                        borderRadius="4px"
                        px={4}
                        py={2}
                        size="sm"
                      >
                        {`${positionData.count - noOfUpdatedPosition} ${
                          positionData.count - noOfUpdatedPosition !== 1 ? t('jobPosition:positions') : t('jobPosition:position')
                        }`}
                      </Button>
                      {isAdministrationAccount && (
                        <Button
                          colorScheme="blue"
                          data-testid="createPositionBtn"
                          style={{ float: 'right' }}
                          fontWeight="bold"
                          fontSize=".9rem"
                          borderRadius="4px"
                          px={4}
                          py={2}
                          size="sm"
                          marginLeft="12px"
                          onClick={() => handlePositionForm()}
                        >
                          {t('jobPosition:createNewPosition')}
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </AccordionButton>
              </h2>
              <AccordionPanel px={0} pt={0} pb={6}>
                <VStack
                  divider={<StackDivider borderColor="#ECEFF1" />}
                  spacing={0}
                  align="stretch"
                  data-testid="positionListWrapper"
                >
                  {!isLoading && positionData.data.length <= 0 && (
                    <Box px={6}>
                      <Heading as="h5" fontSize="md" lineHeight="1" my={4}>
                        {t('jobPosition:noJobs')}
                      </Heading>
                    </Box>
                  )}
                  {(filterByRequisition
                    ? positionData.data.filter((pos) => !pos.isPaused)
                    : positionData.data
                  ).map((position, index) => loadPositionItem(position, index))}
                </VStack>
                {isLoading && (
                  <Center py={6}>
                    <Spinner color="blue.500" />
                  </Center>
                )}
              </AccordionPanel>
            </AccordionItem>
            {positionData.lastPage === positionQueryParams.page || positionData.data.length <= 0 ? (
              ''
            ) : (
              <Center>
                <Button onClick={loadMorePosition} data-testid="loadMoreBtn" colorScheme="blue">
                  {t('jobPosition:loadMore')}
                </Button>
              </Center>
            )}
          </Accordion>
          <Modal onClose={onClose} isOpen={isOpen} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('jobPosition:recruiterFollowing')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {assignedRecruiters ? (
                  <Box>
                    {_sortBy(assignedRecruiters, 'name').map((item: FollowedRecruiters) => loadFollowedRecruiterListItems(item))}
                  </Box>
                ) : (
                  ''
                )}
              </ModalBody>
            </ModalContent>
          </Modal>

          {!_isNil(selectedPosition) && !_isNil(confirmUpdateStatusMessage) && (
            <UpdateStatusConfirmation
              isOpen={isUpdateStatusModalOpen}
              onClose={onUpdateStatusModalClose}
              selectedPosition={selectedPosition}
              updatePositionByStatus={isArchivingPosition || isUnArchivingPosition ? callArchiveHandler : updatePositionByStatus}
              message={confirmUpdateStatusMessage}
              // checkBoxComponent={getInterviewCancelCheckBox()}
              isPositionOpen={/* istanbul ignore next */ selectedPosition.open}
              isPositonArchived={selectedPosition.archived}
              isArchivingPosition={isArchivingPosition}
            />
          )}
        </Fragment>
      )}
      {createNewPosition && (
        <Box ref={positionRef}>
          <NewCreateJobsView
            positionDataToEdit={positionToEdit}
            redirectToPositionAfterCreated={redirectToPositionAfterCreated}
            selectedBusiness={activeBusiness}
            onClose={() => {
              setCloseCopyPosition();
              setPositionToEdit(null);
              setCreateNewPosition(false);
            }}
            createNewPosition={createNewPosition}
            isCopyingPosition={copyPosition}
            onCompleteCopy={onCompleteCopy}
          />
        </Box>
      )}

      {/* {positionToCopy && (
        <CopyPositionButton positionFromPositionList={positionToCopy} isOpen={copyPosition} onClose={onCompleteCopy} />
      )} */}
    </Box>
  );
};
